import type { UIMessage } from "../lib/types";

export type NativeInputState = "queued" | "sending" | "enqueued" | "absorbed" | "uncertain" | "cancelled" | "failed";
export interface NativeInput {
  id: string; userMsgId: string; rootId: string; state: NativeInputState;
  text: string; files: string[]; queuedAt: number; updatedAt: number;
  urgent: boolean; error?: string | null; canCancel: boolean; canSendNow: boolean;
  /** Still in backend custody (its edit window): the words can change. */
  canEdit?: boolean;
  /** ms epoch when it is handed to the agent unless edited/held/sent now. */
  holdUntil?: number;
}
export interface NativeInputSnapshot { rootId: string | null; inputs: NativeInput[]; revision?: number }
const states = new Set<NativeInputState>(["queued", "sending", "enqueued", "absorbed", "uncertain", "cancelled", "failed"]);
/** Invalid/missing snapshots are not evidence that inputs disappeared. */
export function parseNativeInputSnapshot(raw: unknown): NativeInputSnapshot | undefined {
  if (!raw || typeof raw !== "object") return;
  const value = raw as Record<string, unknown>;
  if (!(value.rootId === null || typeof value.rootId === "string") || !Array.isArray(value.inputs)) return;
  const inputs: NativeInput[] = [], ids = new Set<string>();
  for (const row of value.inputs) {
    if (!row || typeof row !== "object" || typeof row.id !== "string" || !row.id ||
        typeof row.userMsgId !== "string" || !row.userMsgId || typeof row.rootId !== "string" ||
        typeof row.text !== "string" || !states.has(row.state) || !Number.isFinite(row.queuedAt) ||
        !Number.isFinite(row.updatedAt) || ids.has(row.userMsgId)) return;
    ids.add(row.userMsgId);
    inputs.push({ id: row.id, userMsgId: row.userMsgId, rootId: row.rootId, state: row.state,
      text: row.text, files: Array.isArray(row.files) ? row.files.filter((f: unknown): f is string => typeof f === "string") : [],
      queuedAt: row.queuedAt, updatedAt: row.updatedAt, urgent: row.urgent === true,
      error: typeof row.error === "string" ? row.error : undefined,
      canCancel: row.canCancel === true && ["queued", "enqueued", "uncertain"].includes(row.state),
      canEdit: row.canEdit === true && row.state === "queued",
      ...(row.state === "queued" && Number.isFinite(row.holdUntil) ? { holdUntil: row.holdUntil } : {}),
      canSendNow: row.canSendNow === true && row.rootId === value.rootId && ["queued", "sending", "enqueued"].includes(row.state) });
  }
  return { rootId: value.rootId, inputs, ...(typeof value.revision === "number" && Number.isFinite(value.revision) ? { revision: value.revision } : {}) };
}
const rank: Record<NativeInputState, number> = { queued: 0, sending: 1, enqueued: 2, uncertain: 3, failed: 4, cancelled: 5, absorbed: 6 };
export function mergeNativeInputSnapshot(previous: NativeInputSnapshot | undefined, incoming: NativeInputSnapshot): NativeInputSnapshot {
  if (!previous) return incoming;
  if (previous.revision !== undefined && incoming.revision !== undefined) return incoming.revision < previous.revision ? previous : incoming;
  const old = new Map(previous.inputs.map(input => [input.userMsgId, input]));
  const newest = Math.max(0, ...incoming.inputs.map(input => input.updatedAt));
  const inputs = incoming.inputs.map(input => {
    const before = old.get(input.userMsgId); old.delete(input.userMsgId);
    return before && (before.updatedAt > input.updatedAt || before.updatedAt === input.updatedAt && rank[before.state] > rank[input.state]) ? before : input;
  });
  // Pre-version backend compatibility: a slow POST must not erase a newer exact-ID receipt.
  for (const input of old.values()) if (input.updatedAt > newest) inputs.push(input);
  inputs.sort((a, b) => a.queuedAt - b.queuedAt);
  return { ...incoming, inputs };
}
/** Only absorbed additions join the transcript, before its one live assistant. */
export function nativeInputMessages(messages: UIMessage[], snapshot?: NativeInputSnapshot, history: readonly UIMessage[] = []): UIMessage[] {
  if (history.some(saved => saved.meta?.nativeInput && !messages.some(message => message.id === saved.id))) messages = [...messages];
  for (const saved of history) {
    if (!saved.meta?.nativeInput || messages.some(message => message.id === saved.id)) continue;
    const at = messages.findIndex(message => message.isStreaming || Date.parse(message.createdAt) > Date.parse(saved.createdAt));
    messages.splice(at < 0 ? messages.length : at, 0, saved);
  }
  const byId = new Map(snapshot?.inputs.map(input => [input.userMsgId, input]) ?? []);
  const result = messages.filter(message => {
    if (message.role !== "user") return true;
    const input = byId.get(message.id);
    return input ? input.state === "absorbed" : !message.meta?.nativeInput || ["absorbed", "uncertain", "failed"].includes(message.meta.nativeInput.state ?? "");
  });
  for (const input of snapshot?.inputs ?? []) {
    if (input.state !== "absorbed") continue;
    const existing = result.findIndex(message => message.id === input.userMsgId);
    const prior = existing >= 0 ? result[existing] : undefined;
    const message: UIMessage = { ...prior, id: input.userMsgId, role: "user", content: input.text, files: input.files,
      createdAt: new Date(input.queuedAt).toISOString(), meta: { ...prior?.meta, type: prior?.meta?.type ?? "Message",
        nativeInput: { ...prior?.meta?.nativeInput, rootId: input.rootId, state: "absorbed" } } };
    if (existing >= 0) { result[existing] = message; continue; }
    const at = result.findIndex(row => row.isStreaming || Date.parse(row.createdAt) > input.queuedAt);
    result.splice(at < 0 ? result.length : at, 0, message);
  }
  return result;
}
